import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import LiteShell from './LiteShell';

/**
 * /us/forget-me — Self-serve GDPR Art. 17 right-to-erasure for Lite users.
 *
 * Hits POST /api/lite/forget-me which deletes everything keyed off the
 * email_hash across waitlist, unlocks, usage, subscriptions, transactions.
 * Also instructs the user to clear their browser localStorage.
 */
export default function LiteForgetMe() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    if (!confirm) {
      setError('Please tick the confirmation box — this cannot be undone.');
      return;
    }
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/lite/forget-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), reason: reason || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not process erasure.');
      }
      const data = await res.json();
      // Clear local browser state too
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('playfirst_'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {/* ignore */}
      setDone(data);
    } catch (e) {
      setError(e.message || 'Could not process erasure.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LiteShell currentStep={1} totalSteps={1}>
      <div className="max-w-xl mx-auto pt-6" data-testid="lite-forget-me-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-rose-700" />
          </div>
          <div>
            <h1 className="text-2xl font-serif text-primary-900 mb-1">Delete my Lite data</h1>
            <p className="text-sm text-stone-600 leading-relaxed">
              Self-serve right-to-erasure (GDPR Art. 17, POPIA, CCPA). One click. Permanent.
            </p>
          </div>
        </div>

        {!done ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 text-sm text-stone-700">
              <p className="font-medium mb-1">What gets deleted</p>
              <ul className="list-disc pl-5 space-y-0.5 text-stone-600">
                <li>Your waitlist record (if you're on it)</li>
                <li>Hashed usage logs and email-unlock history</li>
                <li>Lite Pro subscription record (your subscription will be cancelled)</li>
                <li>All transaction references</li>
              </ul>
              <p className="mt-3 text-xs text-stone-500">Nothing about your clients ever leaves your browser via Lite, so there's nothing on our servers to delete in that regard.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Your email</Label>
              <Input
                type="email" placeholder="you@clinic.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                data-testid="forget-me-email"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Reason (optional, for our records)</Label>
              <Textarea
                placeholder="I no longer use the service, etc."
                value={reason} onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                disabled={loading}
                data-testid="forget-me-reason"
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
              <input
                type="checkbox" checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                className="mt-1"
                disabled={loading}
                data-testid="forget-me-confirm"
              />
              <span>I understand this is permanent and cannot be undone. If I have an active Lite Pro subscription, it will be cancelled (refunds for unused time can be requested via support).</span>
            </label>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2" data-testid="forget-me-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              onClick={handleDelete}
              disabled={loading}
              className="w-full rounded-full bg-rose-600 hover:bg-rose-700"
              data-testid="forget-me-submit"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete my data permanently</>}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 text-center" data-testid="forget-me-done">
            <ShieldCheck className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
            <h2 className="text-xl font-serif text-primary-900 mb-2">Your data has been erased</h2>
            <p className="text-sm text-stone-700 mb-3">
              {Object.entries(done.deleted || {}).map(([k, v]) => `${v} ${k}`).filter((s) => !s.startsWith('0 ')).join(', ') || 'Nothing was found to delete (we may have had nothing on you).'}
            </p>
            <p className="text-xs text-stone-600 leading-relaxed">
              Your browser localStorage has also been cleared. We've kept no backup of any kind.
            </p>
          </div>
        )}
      </div>
    </LiteShell>
  );
}
